import React, { useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Upload, FileText, Image, X, CheckCircle2, Loader2, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MAX_FILE_SIZE = 50 * 1024 * 1024;

export const DocumentUpload = ({ intakeId, onUploadComplete }) => {
  const [govIdFile, setGovIdFile] = useState(null);
  const [caseDocFile, setCaseDocFile] = useState(null);
  const [uploading, setUploading] = useState({ govId: false, caseDoc: false });
  const [uploaded, setUploaded] = useState({ govId: false, caseDoc: false });
  const [errors, setErrors] = useState({ govId: null, caseDoc: null });
  
  const govIdInputRef = useRef(null);
  const caseDocInputRef = useRef(null);

  const validateFile = (file, type) => {
    if (!file) return { valid: false, error: 'No file selected' };
    if (file.size > MAX_FILE_SIZE) {
      return { valid: false, error: `File too large. Maximum size is 50MB.` };
    }
    const allowedExtensions = type === 'gov_id' 
      ? ['.pdf', '.docx', '.doc', '.jpg', '.jpeg', '.png', '.gif', '.webp', '.heic']
      : ['.pdf', '.docx', '.doc', '.txt', '.rtf'];
    const ext = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
    if (!allowedExtensions.includes(ext)) {
      return { valid: false, error: `Invalid file type. Allowed: ${allowedExtensions.join(', ')}` };
    }
    return { valid: true, error: null };
  };

  const uploadFile = async (file, type) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('doc_type', type);

    const response = await axios.post(`${API}/intake/${intakeId}/upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120000
    });
    return response.data;
  };

  const handleFileSelect = async (e, type) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const typeKey = type === 'gov_id' ? 'govId' : 'caseDoc';
    setErrors(prev => ({ ...prev, [typeKey]: null }));

    const validation = validateFile(file, type);
    if (!validation.valid) {
      setErrors(prev => ({ ...prev, [typeKey]: validation.error }));
      toast.error(validation.error);
      if (type === 'gov_id' && govIdInputRef.current) govIdInputRef.current.value = '';
      if (type === 'case_doc' && caseDocInputRef.current) caseDocInputRef.current.value = '';
      return;
    }

    if (type === 'gov_id') {
      setGovIdFile(file);
    } else {
      setCaseDocFile(file);
    }

    setUploading(prev => ({ ...prev, [typeKey]: true }));
    
    try {
      await uploadFile(file, type);
      setUploaded(prev => ({ ...prev, [typeKey]: true }));
      setErrors(prev => ({ ...prev, [typeKey]: null }));
      toast.success(`${type === 'gov_id' ? 'Government ID' : 'Case document'} uploaded successfully`);
    } catch (error) {
      console.error('Upload error:', error);
      let errorMsg = 'Upload failed. Please try again.';
      if (error.code === 'ECONNABORTED') {
        errorMsg = 'Connection timed out. Please try a smaller file.';
      } else if (error.response?.status === 413) {
        errorMsg = 'File is too large.';
      }
      setErrors(prev => ({ ...prev, [typeKey]: errorMsg }));
      toast.error(errorMsg);
    } finally {
      setUploading(prev => ({ ...prev, [typeKey]: false }));
    }
  };

  const removeFile = (type) => {
    if (type === 'gov_id') {
      setGovIdFile(null);
      setUploaded(prev => ({ ...prev, govId: false }));
      setErrors(prev => ({ ...prev, govId: null }));
      if (govIdInputRef.current) govIdInputRef.current.value = '';
    } else {
      setCaseDocFile(null);
      setUploaded(prev => ({ ...prev, caseDoc: false }));
      setErrors(prev => ({ ...prev, caseDoc: null }));
      if (caseDocInputRef.current) caseDocInputRef.current.value = '';
    }
  };

  const getFileIcon = (file) => {
    if (!file) return null;
    const ext = file.name.toLowerCase();
    if (ext.endsWith('.pdf') || ext.endsWith('.doc') || ext.endsWith('.docx')) {
      return <FileText className="w-8 h-8" />;
    }
    return <Image className="w-8 h-8" />;
  };

  // Both documents must be uploaded to proceed
  const canProceed = uploaded.govId && uploaded.caseDoc;

  return (
    <div className="space-y-6" data-testid="document-upload">
      <div className="bg-blue-50 border border-blue-200 p-4 text-sm text-blue-800">
        <p className="font-medium mb-1">Required Documents</p>
        <p>Please upload both your government ID and case documents to continue.</p>
        <p className="mt-2 text-xs">Supported formats: PDF, DOCX, DOC, Images (max 50MB each)</p>
      </div>

      {/* Government ID Upload */}
      <div>
        <h4 className="text-sm font-medium uppercase tracking-widest text-muted-foreground mb-3 flex items-center gap-2">
          Driver's License or Passport
          {uploaded.govId && <CheckCircle2 className="w-4 h-4 text-green-600" />}
          <span className="text-red-500">*</span>
        </h4>
        
        <input
          type="file"
          ref={govIdInputRef}
          onChange={(e) => handleFileSelect(e, 'gov_id')}
          accept=".pdf,.docx,.doc,.jpg,.jpeg,.png,.gif,.webp,.heic"
          className="hidden"
          data-testid="gov-id-input"
        />
        
        {!govIdFile ? (
          <button
            onClick={() => govIdInputRef.current?.click()}
            className="w-full p-6 border-2 border-dashed border-gray-300 hover:border-black transition-colors flex flex-col items-center gap-3"
            data-testid="gov-id-upload-btn"
          >
            <Upload className="w-8 h-8 text-muted-foreground" />
            <span className="text-sm text-muted-foreground">Click to upload Government ID</span>
          </button>
        ) : (
          <div className={`p-4 border ${errors.govId ? 'border-red-300 bg-red-50' : uploaded.govId ? 'border-green-300 bg-green-50' : 'border-gray-200'}`}>
            <div className="flex items-center gap-4">
              {uploading.govId ? (
                <Loader2 className="w-8 h-8 animate-spin" />
              ) : uploaded.govId ? (
                <CheckCircle2 className="w-8 h-8 text-green-600" />
              ) : errors.govId ? (
                <AlertCircle className="w-8 h-8 text-red-500" />
              ) : (
                getFileIcon(govIdFile)
              )}
              <div className="flex-1 min-w-0">
                <p className="font-medium truncate">{govIdFile.name}</p>
                <p className="text-sm text-muted-foreground">{(govIdFile.size / 1024 / 1024).toFixed(2)} MB</p>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => removeFile('gov_id')}
                disabled={uploading.govId}
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
            {errors.govId && <p className="mt-2 text-sm text-red-600">{errors.govId}</p>}
          </div>
        )}
      </div>

      {/* Case Document Upload */}
      <div>
        <h4 className="text-sm font-medium uppercase tracking-widest text-muted-foreground mb-3 flex items-center gap-2">
          Origin Case File
          {uploaded.caseDoc && <CheckCircle2 className="w-4 h-4 text-green-600" />}
          <span className="text-red-500">*</span>
        </h4>
        <p className="text-xs text-muted-foreground mb-3">
          Original foreclosure notice, criminal complaint, arrest warrant, etc.
        </p>
        
        <input
          type="file"
          ref={caseDocInputRef}
          onChange={(e) => handleFileSelect(e, 'case_doc')}
          accept=".pdf,.docx,.doc,.txt,.rtf"
          className="hidden"
          data-testid="case-doc-input"
        />
        
        {!caseDocFile ? (
          <button
            onClick={() => caseDocInputRef.current?.click()}
            className="w-full p-6 border-2 border-dashed border-gray-300 hover:border-black transition-colors flex flex-col items-center gap-3"
            data-testid="case-doc-upload-btn"
          >
            <Upload className="w-8 h-8 text-muted-foreground" />
            <span className="text-sm text-muted-foreground">Click to upload Case Document</span>
          </button>
        ) : (
          <div className={`p-4 border ${errors.caseDoc ? 'border-red-300 bg-red-50' : uploaded.caseDoc ? 'border-green-300 bg-green-50' : 'border-gray-200'}`}>
            <div className="flex items-center gap-4">
              {uploading.caseDoc ? (
                <Loader2 className="w-8 h-8 animate-spin" />
              ) : uploaded.caseDoc ? (
                <CheckCircle2 className="w-8 h-8 text-green-600" />
              ) : errors.caseDoc ? (
                <AlertCircle className="w-8 h-8 text-red-500" />
              ) : (
                getFileIcon(caseDocFile)
              )}
              <div className="flex-1 min-w-0">
                <p className="font-medium truncate">{caseDocFile.name}</p>
                <p className="text-sm text-muted-foreground">{(caseDocFile.size / 1024 / 1024).toFixed(2)} MB</p>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => removeFile('case_doc')}
                disabled={uploading.caseDoc}
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
            {errors.caseDoc && <p className="mt-2 text-sm text-red-600">{errors.caseDoc}</p>}
          </div>
        )}
      </div>

      {/* Continue Button */}
      <div className="pt-4 border-t border-gray-200">
        {canProceed ? (
          <div className="space-y-4">
            <div className="bg-green-50 border border-green-200 p-3 text-sm text-green-800 flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5" />
              Both documents uploaded successfully!
            </div>
            <Button
              onClick={onUploadComplete}
              className="w-full rounded-none bg-black text-white hover:bg-black/90 py-6 text-lg"
              data-testid="continue-btn"
            >
              <CheckCircle2 className="w-5 h-5 mr-2" />
              Continue to Authorization
            </Button>
          </div>
        ) : (
          <div className="text-center text-sm text-muted-foreground">
            <p>Please upload both required documents to continue</p>
            {(uploaded.govId || uploaded.caseDoc) && (
              <p className="mt-2">
                {uploaded.govId ? '✓ Government ID uploaded' : '○ Government ID required'} • {' '}
                {uploaded.caseDoc ? '✓ Case document uploaded' : '○ Case document required'}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default DocumentUpload;
